import React, { ReactNode, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Flashbar,
  Header,
  Pagination,
  SpaceBetween,
  StatusIndicator,
  Table,
  TextContent,
  TextFilter,
} from '@amzn/awsui-components-react-v3';
import {
  CartAccessItem,
  CONSUMER_AWS_ACCOUNT_IS_ISENGARD,
  CONSUMER_AWS_ACCOUNT_IS_PRODUCTION,
  getResource,
  ONLY_ISENGARD_ACCOUNTS_CAN_REQUEST_ACCESS,
  ONLY_ISENGARD_PROD_ACCOUNTS_CAN_REQUEST_ACCESS,
  setNotificationMessages,
} from 'src/components/common/checkout/common';
import {
  FGA_ITEM,
  LAKE_FORMATION_TAG_ITEM,
  NON_ISENGARD_ACCESS_GLADSTONE_ERROR_PREFIX,
  NON_ISENGARD_ACCESS_WIKI_URL,
  NON_PRODUCTION_ACCESS_GLADSTONE_ERROR_PREFIX,
  NON_PRODUCTION_ACCESS_WIKI_URL,
  REDSHIFT_TAG_ITEM,
  RESOURCE_ITEM,
} from 'src/commons/constants';
import { validatePrerequisites } from 'src/api/permissions';
import { ValidatePrerequisitesResult, ValidationDetail } from 'aws-sdk/clients/awsdatalakegladstonelambda';
import { Link } from 'src/components/common/Link';

export interface ValidatePrerequisitesEditorProps {
  cartItems: CartAccessItem[];
  activeGroup: string;
  activeWorkspace: any;
  setValidationErrorMessage: any;
}

export interface ValidationRecord {
  validatedResource: string;
  isValid: boolean;
  cause?: string;
}

export interface ResourceValidation {
  resource: string;
  validationRecords: ValidationRecord[];
}

export const allChecksPass = (resourceValidation: ResourceValidation): boolean => {
  if (!resourceValidation || resourceValidation.validationRecords.length === 0) {
    return true;
  }
  return !resourceValidation.validationRecords.some(
    (record) =>
      !record.isValid &&
      (record.validatedResource == ONLY_ISENGARD_ACCOUNTS_CAN_REQUEST_ACCESS ||
        record.validatedResource == ONLY_ISENGARD_PROD_ACCOUNTS_CAN_REQUEST_ACCESS),
  );
};

export const ValidatePrerequisitesEditor = (props: ValidatePrerequisitesEditorProps) => {
  const [resourceValidations, setResourceValidations] = useState([] as ResourceValidation[]);
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [errors, setErrors] = useState([] as string[]);
  const [loading, setLoading] = useState(false);
  const [filteringText, setFilteringText] = useState('');
  const [canPerformValidation, setCanPerformValidation] = useState(true);
  const [notifications, setNotifications] = useState([]);

  const isWorkspace = !!props.activeWorkspace;
  const currentOwnerId = isWorkspace ? props.activeWorkspace.workspaceId : props.activeGroup;

  useEffect(() => {
    // validate if there are any resources to validate
    if (props.cartItems.length === 0) {
      setCanPerformValidation(false);
      return;
    }
    const filterCondition = (item: CartAccessItem): boolean =>
      item &&
      item.dataLakePrincipal &&
      item.region &&
      item.owner &&
      (item.itemType === REDSHIFT_TAG_ITEM ||
        item.itemType === LAKE_FORMATION_TAG_ITEM ||
        item.itemType === RESOURCE_ITEM ||
        item.itemType === FGA_ITEM);

    // can perform validation only for LF/DZ/Redshift access type, other than IAM.
    const canPerformValidation = props.cartItems.some((item) => filterCondition.call(this, item));
    setCanPerformValidation(canPerformValidation);
    if (canPerformValidation) {
      setLoading(true);
      validateResources(props.cartItems.filter((item) => filterCondition.call(this, item))).finally(() =>
        setLoading(false),
      );
    }
  }, [props.cartItems]);

  // Filter resources based on filtering text
  const filteredResources = React.useMemo(() => {
    if (!filteringText) return resourceValidations;
    const lowerCaseFilter = filteringText.toLowerCase();
    return resourceValidations.filter((resourceVal) => resourceVal.resource.toLowerCase().includes(lowerCaseFilter));
  }, [resourceValidations, filteringText]);
  // Calculate number of pages
  const pagesCount = Math.max(1, Math.ceil(filteredResources.length / 10)); // Assuming 10 items per page

  const reverseStringWithDots = (input: string): string => {
    if (!input.includes('.')) {
      return input;
    }
    return input.split('.').reverse().join('.');
  };

  const validateAndSetGlobalErrorNotifications = async (validatePrerequisitesResult: ValidatePrerequisitesResult) => {
    const nonIsengardValidation: ValidationDetail =
      validatePrerequisitesResult?.lakeFormationAccessRequestPrerequisitesResult?.lakeFormationAccessRequestPrerequisitesValidations.find(
        (record) => !record.isValid && record.validatedResource == ONLY_ISENGARD_ACCOUNTS_CAN_REQUEST_ACCESS,
      );
    if (nonIsengardValidation) {
      const errMsg = nonIsengardValidation.cause?.startsWith(NON_ISENGARD_ACCESS_GLADSTONE_ERROR_PREFIX)
        ? nonIsengardValidation.cause?.split(NON_ISENGARD_ACCESS_GLADSTONE_ERROR_PREFIX)[1] +
          ' is not managed by Isengard. '
        : nonIsengardValidation.cause;
      let notificationContent = (
        <TextContent>
          {errMsg}
          <Link external href={NON_ISENGARD_ACCESS_WIKI_URL}>
            Learn more
          </Link>
        </TextContent>
      );
      setValidationErrorWithWrapper(notificationContent);
    }

    const nonIsengardProdValidation: ValidationDetail =
      validatePrerequisitesResult?.lakeFormationAccessRequestPrerequisitesResult?.lakeFormationAccessRequestPrerequisitesValidations.find(
        (record) => !record.isValid && record.validatedResource == ONLY_ISENGARD_PROD_ACCOUNTS_CAN_REQUEST_ACCESS,
      );
    if (nonIsengardProdValidation) {
      const errMsg = nonIsengardProdValidation.cause?.startsWith(NON_PRODUCTION_ACCESS_GLADSTONE_ERROR_PREFIX)
        ? nonIsengardProdValidation.cause?.split(NON_PRODUCTION_ACCESS_GLADSTONE_ERROR_PREFIX)[1] +
          ' is not a production account. '
        : nonIsengardProdValidation.cause;
      let notificationContent = (
        <TextContent>
          {errMsg}
          <Link external href={NON_PRODUCTION_ACCESS_WIKI_URL}>
            Learn more
          </Link>
        </TextContent>
      );
      setValidationErrorWithWrapper(notificationContent);
    }
  };

  const setValidationErrorWithWrapper = (content: ReactNode) => {
    props.setValidationErrorMessage(
      // this will only ever display one error at a time, but a list still looks better.
      <TextContent>
        Please fix the following issues before proceeding:
        <br />
        <ul>
          <li>{content}</li>
        </ul>
      </TextContent>,
    );
  };

  // Validate resources
  const validateResources = async (items: CartAccessItem[]) => {
    const validationErrors = [];
    const validationRecords: ResourceValidation[] = [];
    for (const item of items) {
      const resourceId = reverseStringWithDots(item.id);
      try {
        const resource = getResource(item);
        const validatePrerequisitesResult = await validatePrerequisites({
          lakeFormationAccessRequestPrerequisites: {
            resource: resource,
            dataLakePrincipal: item.dataLakePrincipal,
            dataLocationRegion: item.region,
            ownerRequestedTo: item.owner,
            // optional fields below
            publisherDataPermissionId: item.permissionId,
            dataPermissionConsumerRole: isWorkspace ? props.activeWorkspace?.workspaceRoleArn : undefined,
          },
          ownerId: currentOwnerId,
        });
        validationRecords.push({
          resource: resourceId,
          validationRecords:
            validatePrerequisitesResult?.lakeFormationAccessRequestPrerequisitesResult?.lakeFormationAccessRequestPrerequisitesValidations.map(
              (record) => {
                return {
                  validatedResource: record.validatedResource,
                  isValid: record.isValid,
                  cause: record.cause,
                };
              },
            ),
        });
        // check and display global validation errors.
        validateAndSetGlobalErrorNotifications(validatePrerequisitesResult);
      } catch (e) {
        console.error(`Failed to validate for resource: ${item.id}`, e);
        validationErrors.push(
          <TextContent>
            Resource <b>{resourceId}</b> failed with cause: {e.message}
          </TextContent>,
        );
      }
    }
    setResourceValidations(validationRecords);
    setErrors(validationErrors);
  };

  // Handle pagination change
  const handlePaginationChange = ({ detail }) => {
    setCurrentPageIndex(detail.currentPageIndex);
  };

  // Handle filter change
  const handleFilterChange = ({ detail }) => {
    setFilteringText(detail.filteringText);
  };

  function getValidationResult(validateResType: string) {
    return (item) => {
      const isValidationPresent = item.validationRecords.some((record) => record.validatedResource == validateResType);
      const isPassed = item.validationRecords.some(
        (record) => record.isValid && record.validatedResource == validateResType,
      );
      const failureMsg = item.validationRecords.filter(
        (record) => !record.isValid && record.validatedResource == validateResType,
      )?.[0]?.cause;
      const iconType = isValidationPresent ? (isPassed ? 'success' : 'error') : 'info';
      const message = isValidationPresent ? (isPassed ? 'Passed' : failureMsg) : 'N/A';
      return <StatusIndicator type={iconType}>{message}</StatusIndicator>;
    };
  }

  return (
    <>
      <Flashbar items={notifications} />
      <SpaceBetween size='m' direction={'vertical'}>
        <Header
          description='The following validations must pass before you can request access for these resources.'
          variant='h3'
        >
          Access request prerequisites
        </Header>
        {errors.length > 0 && (
          <Alert type={'error'} header={'Failed to validate prerequisites'}>
            <TextContent>
              There was an error when validating prerequisites:
              <ul>
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </TextContent>
          </Alert>
        )}

        <SpaceBetween size='l'>
          {!canPerformValidation && (
            <Alert header={'Validation Passed'} type={'success'}>
              {'There are no prerequisites for access request!'}
            </Alert>
          )}
          {canPerformValidation && (
            <Table
              columnDefinitions={[
                {
                  cell: (item) => <>{item.resource}</>,
                  header: 'Resource',
                  id: 'resource',
                },
                {
                  cell: (item) => {
                    const isPassed = allChecksPass(item);
                    return (
                      <StatusIndicator type={isPassed ? 'success' : 'error'}>
                        {isPassed ? 'Passed' : 'Failed'}
                      </StatusIndicator>
                    );
                  },
                  header: 'Overall status',
                  id: 'status',
                },
                {
                  cell: getValidationResult(CONSUMER_AWS_ACCOUNT_IS_ISENGARD),
                  header: 'Is Isengard account',
                  id: 'isengardAccount',
                },
                {
                  cell: getValidationResult(CONSUMER_AWS_ACCOUNT_IS_PRODUCTION),
                  header: 'Is production account',
                  id: 'productionAccount',
                },
                {
                  cell: getValidationResult(ONLY_ISENGARD_ACCOUNTS_CAN_REQUEST_ACCESS),
                  header: 'Only Isengard accounts allowed',
                  id: 'onlyIsengardAllowed',
                },
                {
                  cell: getValidationResult(ONLY_ISENGARD_PROD_ACCOUNTS_CAN_REQUEST_ACCESS),
                  header: 'Only Isengard production accounts allowed',
                  id: 'onlyprodAllowed',
                },
              ]}
              empty={
                <Box color='inherit' margin={{ vertical: 'xs' }} textAlign='center'>
                  <SpaceBetween size='m'>
                    <b>No resources</b>
                  </SpaceBetween>
                </Box>
              }
              filter={
                <TextFilter
                  filteringPlaceholder='Find resources'
                  filteringText={filteringText}
                  onChange={handleFilterChange}
                />
              }
              header={
                <Header description='Status of validations for Lake Formation access requests across multiple resources.'>
                  Validation status
                </Header>
              }
              items={filteredResources}
              loadingText='Loading'
              loading={loading}
              pagination={
                <Pagination
                  currentPageIndex={currentPageIndex}
                  pagesCount={pagesCount}
                  onChange={handlePaginationChange}
                />
              }
              trackBy='resource'
            />
          )}
        </SpaceBetween>
      </SpaceBetween>
    </>
  );
};
